"use client"

import { useState } from "react"

import { useTranslations } from "next-intl"

import { PdpGalleryViewer } from "@/components/marketplace/pdp/gallery-viewer"
import { WishlistHeart } from "@/components/marketplace/product/wishlist-heart"
import { ArrowDisc, CarouselDots, IconButton, Reel } from "@/components/ui"
import { SchemeScope } from "@/components/ui/scheme-scope"
import { PRODUCT_PHOTO_QUALITY, TileImage } from "@/components/ui/tile-image"
import { useReelPagination } from "@/lib/hooks/use-reel-pagination"

interface PdpGalleryProps {
  images: string[]
  title: string
  productId?: string
  className?: string
}

const DOT_WINDOW = 5

export const PdpGallery = ({ images, title, productId, className }: PdpGalleryProps) => {
  const t = useTranslations("pdp")
  const { ref, index, goTo } = useReelPagination(images.length)
  const [viewerOpen, setViewerOpen] = useState(false)
  const paged = images.length > 1
  const stageAspect = "aspect-square"
  return (
    <div className={`flex flex-col @3xl:flex-row-reverse @3xl:items-start @3xl:gap-250 ${className ?? ""}`}>
      <SchemeScope
        scheme="light"
        className="bg-surface-subtle mx-gut-bleed pt-safe rounded-b-12x @3xl:rounded-12x @3xl:shadow-stage relative min-w-0 flex-1 overflow-hidden @3xl:mx-0 @3xl:aspect-square @3xl:pt-0"
      >
        <Reel
          ref={ref}
          snap
          gap="0"
          role="group"
          tabIndex={0}
          className={`focus-visible:ring-brand snap-x snap-mandatory focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset ${stageAspect}`}
          aria-label={t("gallery.imagesOf", { title })}
        >
          {images.map((src, slideIndex) => (
            <div key={src} className={`bg-surface-subtle relative w-full shrink-0 snap-start ${stageAspect}`}>
              <TileImage
                src={src}
                alt={t("gallery.imageAlt", { title, index: slideIndex + 1 })}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                quality={PRODUCT_PHOTO_QUALITY}
                className="object-contain mix-blend-multiply"
                {...(slideIndex === 0 ? { fetchPriority: "high" as const, loading: "eager" as const } : {})}
              />
              <IconButton
                press={false}
                tabIndex={slideIndex === index ? 0 : -1}
                aria-label={t("gallery.openViewer")}
                onClick={() => setViewerOpen(true)}
                className="focus-visible:ring-brand absolute inset-0 size-full cursor-zoom-in rounded-none border-0 bg-transparent hover:bg-transparent focus-visible:ring-2 focus-visible:ring-inset"
              />
            </div>
          ))}
        </Reel>
        {productId && <WishlistHeart size="stage" productId={productId} className="top-safe-offset-3.75 absolute left-3.75 @3xl:top-500 @3xl:left-500" />}
        {paged && (
          <div className="pointer-events-none absolute inset-x-0 bottom-3.75 flex justify-center @3xl:hidden">
            <CarouselDots
              count={images.length}
              index={index}
              onChange={goTo}
              tone="plain"
              activeColor="bg-brand"
              idleColor="bg-line-dot"
              window={DOT_WINDOW}
              className="pointer-events-auto"
            />
          </div>
        )}
        {paged && (
          <div className="absolute right-500 bottom-500 hidden gap-250 @3xl:flex">
            <ArrowDisc direction="previous" tone="raised" size="stage" aria-label={t("gallery.prevImage")} disabled={index === 0} onClick={() => goTo(index - 1)} />
            <ArrowDisc direction="next" tone="raised" size="stage" aria-label={t("gallery.nextImage")} disabled={index === images.length - 1} onClick={() => goTo(index + 1)} />
          </div>
        )}
      </SchemeScope>
      {paged && (
        <div className="relative hidden w-19.5 shrink-0 self-stretch @3xl:block">
          <div
            role="group"
            aria-label={t("gallery.thumbnails")}
            className="absolute inset-y-0 -right-300 left-0 flex scrollbar-none flex-col gap-125 overflow-y-auto pr-300 pb-300"
          >
            {images.map((src, slideIndex) => (
              <Thumb key={src} src={src} label={t("gallery.viewImage", { index: slideIndex + 1 })} current={slideIndex === index} onSelect={() => goTo(slideIndex)} />
            ))}
          </div>
        </div>
      )}
      <PdpGalleryViewer images={images} title={title} index={index} onIndexChange={goTo} open={viewerOpen} onClose={() => setViewerOpen(false)} />
    </div>
  )
}

interface ThumbProps {
  src: string
  label: string
  current: boolean
  onSelect: () => void
}

const Thumb = ({ src, label, current, onSelect }: ThumbProps) => (
  <IconButton
    icon={<TileImage src={src} alt={label} fill sizes="160px" className="object-contain mix-blend-multiply" />}
    aria-label={label}
    aria-current={current}
    onClick={onSelect}
    className={`rounded-10x bg-surface-paper shadow-thumb duration-moderate ease-productive after:rounded-10x relative size-19.5 shrink-0 overflow-hidden border-0 after:pointer-events-none after:absolute after:inset-0 after:ring-1 after:transition-colors after:ring-inset ${current ? "after:ring-brand" : "hover:after:ring-line after:ring-transparent"}`}
  />
)
